import Vue from 'vue';
import axios from "axios";
import {reapplyKeyOnMarkets} from '@/services/keyLiveReapply.service';

const COOLDOWN_MS = 180000; // 3 minutes cooldown

// --- MODULE-LEVEL LOOKUP TABLES (not in Vuex state for performance) ---
let featuredEventsLookup = {};
let featuredOddsLookup = {};

let _typeChangeResetTimer = null;
const TYPE_CHANGE_RESET_DELAY = 2500;

// version 3.2  Monotonic Shield Fix

// --- HELPER: MONOTONIC UPDATE ---
function extractLastUpdate(payload) {
    if (!payload) return 0;
    if (payload.lastUpdate) return new Date(payload.lastUpdate).getTime();
    if (payload.livescore) {
        const ls = Array.isArray(payload.livescore) ? payload.livescore[0] : payload.livescore;
        if (ls && ls.lastUpdate) return new Date(ls.lastUpdate).getTime();
    }
    return 0;
}

function isPayloadStale(existing, incoming) {
    if (!existing || !incoming) return false;

    // 1. STATUS MONOTONICITY
    const exStatus = parseInt(existing.status || '0');
    let inStatus = parseInt(incoming.status || '0');
    if (inStatus === 0 && incoming.livescore) {
        const inLs = Array.isArray(incoming.livescore) ? incoming.livescore[0] : incoming.livescore;
        inStatus = parseInt(inLs?.scoreboard?.status || '0');
    }

    if ((inStatus === 1 || inStatus === 9) && (exStatus === 2 || exStatus === 3)) return true;
    if (inStatus === 2 && exStatus === 3) return true;

    // 2. TIMESTAMP MONOTONICITY
    const exTime = extractLastUpdate(existing);
    const inTime = extractLastUpdate(incoming);
    if (exTime > 0 && inTime > 0 && inTime < exTime) return true;

    return false;
}

function _scheduleTypeChangeReset(commit) {
    if (_typeChangeResetTimer) clearTimeout(_typeChangeResetTimer);
    _typeChangeResetTimer = setTimeout(() => {
        commit('RESET_TYPE_CHANGES');
        _typeChangeResetTimer = null;
    }, TYPE_CHANGE_RESET_DELAY);
}

function _mapEventToLookup(event) {
    if (!event || !event.id) return;
    featuredEventsLookup[String(event.id)] = event;
    if (Array.isArray(event.odds)) {
        event.odds.forEach(market => {
            if (!market || !Array.isArray(market.outcome)) return;
            market.outcome.forEach(odd => {
                if (odd.unique_id) {
                    featuredOddsLookup[odd.unique_id] = {odd, market, eventId: String(event.id)};
                }
            });
        });
    }
}

function _clearLookups() {
    featuredEventsLookup = {};
    featuredOddsLookup = {};
}

const state = {
    events: [],
    loading: false,
    hasInitialFetch: false,
    lastFetchTime: null,
    suggestedPollMs: null,
    featuredTournaments: [],
    featuredTournamentsLoading: false,
};

const getters = {
    getFeaturedEvents: (state) => state.events,
    isLoadingFeatured: (state) => state.loading,
    hasFeaturedInitialFetch: (state) => state.hasInitialFetch,
    getSuggestedPollMs: (state) => state.suggestedPollMs,
    getFeaturedTournaments: (state) => state.featuredTournaments,
    getFeaturedTournamentsLoading: (state) => state.featuredTournamentsLoading,
};

const mutations = {
    SET_LOADING(state, status) {
        state.loading = status;
    },
    SET_DATA(state, events) {
        const incomingById = {};
        events.forEach(function (e) {
            if (String(e.status) !== '3') incomingById[String(e.id)] = e;
        });

        for (var i = state.events.length - 1; i >= 0; i--) {
            var existing = state.events[i];
            var incoming = incomingById[String(existing.id)];
            if (!incoming) {
                state.events.splice(i, 1);
            } else {
                if (!isPayloadStale(existing, incoming)) {
                    existing.livescore = incoming.livescore;
                    existing.status = incoming.status;
                    existing.lastUpdate = incoming.lastUpdate;
                }

                // Le quote e i metadati li aggiorniamo (gestiti granulamente dal socket)
                existing.odds = incoming.odds;
                existing.marketsNum = incoming.marketsNum;
                existing.count = incoming.count;
                existing.sort = incoming.sort;
                existing.earlyPayout = incoming.earlyPayout;
                incomingById[String(existing.id)] = null;
            }
        }

        events.forEach(function (e) {
            if (incomingById[String(e.id)] !== null) {
                state.events.push(e);
            }
        });

        state.events.sort(function (a, b) {
            return (a.sort || 0) - (b.sort || 0);
        });

        _clearLookups();
        state.events.forEach(_mapEventToLookup);
    },
    SET_LAST_FETCH(state, time) {
        state.lastFetchTime = time;
    },
    RESET_DATA(state) {
        state.events = [];
        _clearLookups();
    },
    SET_INITIAL_FETCH_DONE(state) {
        state.hasInitialFetch = true;
    },
    SET_META(state, meta) {
        state.suggestedPollMs = (meta && meta.suggestedPollMs) ? meta.suggestedPollMs : null;
    },

    UPDATE_ODDS_FROM_SOCKET(state, payload) {
        if (!Array.isArray(payload)) return;

        // Same dirty-set pattern used by the other 4 socket consumers.
        const dirtyMarkets = new Set();

        payload.forEach(eventPayload => {
            if (!eventPayload?.odds?.set) return;

            eventPayload.odds.set.forEach(changeItem => {
                const uniqueId = changeItem.unique_id;
                const attr = changeItem._attributes;
                const lookupRef = featuredOddsLookup[uniqueId];

                if (lookupRef && attr) {
                    const {odd: targetOdd, market: targetMarket} = lookupRef;

                    const inTime = attr.lastUpdate ? new Date(attr.lastUpdate).getTime() : 0;
                    if (targetOdd.lastUpdate && inTime > 0 && inTime < targetOdd.lastUpdate) return;

                    const newValue = parseFloat(attr.odds);
                    const oldValue = parseFloat(targetOdd.outcome_odd_value);

                    if (newValue > oldValue) Vue.set(targetOdd, 'type_change', 'up');
                    else if (newValue < oldValue) Vue.set(targetOdd, 'type_change', 'down');

                    // Socket carries the RAW provider value → baseline + display.
                    targetOdd.outcome_odd_original = newValue;
                    targetOdd.outcome_odd_value = newValue;
                    targetOdd.status = parseInt(attr.status);
                    // Freeze-suspend authoritative — task 13.
                    if (targetOdd.frozenApplied !== true) {
                        targetOdd.active = (targetOdd.status === 1);
                    }
                    // Boost invalidation on socket tick — raw provider odd
                    // supersedes any previously-applied boost until a
                    // full-refresh from the server confirms it via a fresh
                    // resolver pass. Prevents a stale "+N%" chip sticking
                    // when the boost rule no longer applies (e.g. context
                    // shifted prematch → live and no live rule exists).
                    if (targetOdd.boostApplied === true) {
                        Vue.set(targetOdd, 'boostApplied', false);
                        Vue.set(targetOdd, 'boostPercent', null);
                        Vue.set(targetOdd, 'oddsPreBoost', null);
                    }
                    if (inTime > 0) targetOdd.lastUpdate = inTime;

                    if (targetMarket && attr.line) targetMarket.specifier = attr.line;
                    // Early bailout — R.1 perf prevention. Only add to the
                    // dirty set markets that carry a KEY rule; the rest
                    // wouldn't get rescaled anyway and calling into the
                    // service would burn a function frame + property read
                    // for a guaranteed no-op.
                    if (targetMarket && targetMarket.keyApplied != null) dirtyMarkets.add(targetMarket);
                }
            });
        });

        if (dirtyMarkets.size > 0) reapplyKeyOnMarkets(dirtyMarkets);
    },

    UPDATE_LIVESCORE_FROM_SOCKET(state, payload) {
        const updates = Array.isArray(payload) ? payload : [payload];
        for (let i = 0; i < updates.length; i++) {
            const change = updates[i];
            const event = featuredEventsLookup[change.id];
            if (!event) continue;

            if (isPayloadStale(event, change)) continue;

            if (change.lastUpdate) Vue.set(event, 'lastUpdate', change.lastUpdate);
            if (change.status != null) event.status = String(change.status);

            if (change.livescore) {
                const incomingData = Array.isArray(change.livescore) ? change.livescore[0] : change.livescore;
                if (!incomingData) continue;

                if (!event.livescore) {
                    Vue.set(event, 'livescore', incomingData);
                } else {
                    if (incomingData.scoreboard) {
                        Vue.set(event.livescore, 'scoreboard',
                            Object.assign({}, event.livescore.scoreboard || {}, incomingData.scoreboard)
                        );
                    }
                    if (incomingData.lastUpdate) Vue.set(event.livescore, 'lastUpdate', incomingData.lastUpdate);
                    if (incomingData.periods) Vue.set(event.livescore, 'periods', incomingData.periods);
                    if (incomingData.statistics) Vue.set(event.livescore, 'statistics', incomingData.statistics);
                    if (incomingData.matchStatus) Vue.set(event.livescore, 'matchStatus', incomingData.matchStatus);
                }
            }
        }
    },

    SET_FEATURED_TOURNAMENTS(state, tournaments) {
        state.featuredTournaments = tournaments;
    },
    SET_FEATURED_TOURNAMENTS_LOADING(state, value) {
        state.featuredTournamentsLoading = value;
    },

    RESET_TYPE_CHANGES() {
        for (const key in featuredOddsLookup) {
            const ref = featuredOddsLookup[key];
            if (ref?.odd?.type_change != null) {
                Vue.set(ref.odd, 'type_change', null);
            }
        }
    }
};

const actions = {
    async fetchFeaturedEventsV3(context, payload) {
        const p = payload || {};
        const force = p.force || false;
        const now = Date.now();

        if (!force && context.state.lastFetchTime) {
            if (now - context.state.lastFetchTime < COOLDOWN_MS) return;
        }

        context.commit('SET_LOADING', true);

        const requestPayload = {
            lang: p.lang || 'en',
            mobilePlatform: p.mobilePlatform || false,
            platformId: context.rootGetters['settings/getPlatformId']
        };

        const apiUrl = context.rootGetters['settings/getUrlApi'];
        const token = context.rootGetters['settings/getToken'];
        const url = `${apiUrl}/v3/events/prematch/featured`;

        const headers = {Authorization: `Bearer ${token}`};

        try {
            const response = await axios.post(url, requestPayload, {headers});
            const data = response.data;
            context.commit('SET_LAST_FETCH', Date.now());

            if (data && data.data && data.data.length > 0) {
                context.commit('SET_DATA', data.data);
            } else {
                context.commit('RESET_DATA');
            }
        } catch (error) {
            console.error("Error in fetchFeaturedEventsV3:", error);
            context.commit('RESET_DATA');
        } finally {
            context.commit('SET_LOADING', false);
        }
    },

    async fetchMixedFeaturedEventsV3(context, payload) {
        const p = payload || {};
        const isInitialLoad = !context.state.hasInitialFetch;

        if (isInitialLoad) {
            context.commit('SET_LOADING', true);
        }

        const requestPayload = {
            lang: p.lang || 'en',
            mobilePlatform: p.mobilePlatform || false,
            platformId: context.rootGetters['settings/getPlatformId']
        };

        const apiUrl = context.rootGetters['settings/getUrlApi'];
        const token = context.rootGetters['settings/getToken'];
        const url = `${apiUrl}/v3/events/mixed/featured`;

        const headers = {Authorization: `Bearer ${token}`};

        try {
            const response = await axios.post(url, requestPayload, {headers});
            const data = response.data;

            context.commit('SET_LAST_FETCH', Date.now());
            context.commit('SET_META', data.meta || null);

            if (data && data.data && data.data.length > 0) {
                context.commit('SET_DATA', data.data);
            } else {
                context.commit('RESET_DATA');
            }
            return data.meta || null;
        } catch (error) {
            console.error("Error in fetchMixedFeaturedEventsV3:", error);
            if (isInitialLoad) {
                context.commit('RESET_DATA');
            }
            return null;
        } finally {
            context.commit('SET_INITIAL_FETCH_DONE');
            if (isInitialLoad) {
                context.commit('SET_LOADING', false);
            }
        }
    },

    resetFeaturedLookup({commit}) {
        commit('RESET_DATA');
    },

    socketUpdateOdds({commit}, payload) {
        commit('UPDATE_ODDS_FROM_SOCKET', payload);
        _scheduleTypeChangeReset(commit);
    },

    async loadFeaturedTournaments({ commit, rootGetters, dispatch }, params = {}) {
        const apiUrl     = rootGetters['settings/getUrlApi'];
        const token      = rootGetters['settings/getToken'];
        const platformId = rootGetters['settings/getPlatformId'];
        const language   = rootGetters['settings/getLanguage'];
        const isMobile   = params.isMobile || false;

        commit('SET_FEATURED_TOURNAMENTS_LOADING', true);
        try {
            const res = await axios.get(
                `${apiUrl}/v3/tournaments/featured/${platformId}?language=${language}&hasEvents=true`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            const tournaments = res.data?.data ?? [];
            commit('SET_FEATURED_TOURNAMENTS', tournaments);

            await Promise.all(tournaments.map(t =>
                dispatch('previewPrematch/fetchFeaturedPreviewV3', {
                    source:               t.tournamentId + '#prematch',
                    tournament:           t.tournamentId,
                    sportId:             t.sportId,
                    categoryId:          t.categoryId ?? null,
                    type:                'previewOdds',
                    mobilePlatform:      isMobile,
                    groupOddsWithSpreads: true,
                    forceDefaultGameConsole: false,
                    mode:                'substitute',
                }, { root: true })
            ));
        } catch (e) {
            console.error('[featuredEvents] loadFeaturedTournaments error:', e);
        } finally {
            commit('SET_FEATURED_TOURNAMENTS_LOADING', false);
        }
    },
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations
};